import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, merge, Subject, Subscription, switchMap, takeUntil, tap, timer } from 'rxjs';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';


@Component({
    selector: 'agpr-session-expiry-confirm-dialog',
    templateUrl: './session-expiry-confirm-dialog.component.html',
    styleUrls: ['./session-expiry-confirm-dialog.component.scss'],
    standalone: false
})
export class SessionExpiryConfirmDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    private static readonly TIMER_INTERVAL_MS = 1000;
    private static readonly TIMER_DELAY_MS = 1000;
    private static readonly TIMER_LENGTH_S = 30;

    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly componentStyle = AgprComponentStyleEnum;

    @Input() id: string;

    @Output() timerExpired: EventEmitter<void> = new EventEmitter();
    @Output() sessionExtended: EventEmitter<void> = new EventEmitter();

    private subscriptions: Subscription[] = [];

    private expiryInitialization$ = new Subject<void>();
    private expiryTimer$ = this.expiryInitialization$.pipe(
        switchMap(() => timer(SessionExpiryConfirmDialogComponent.TIMER_DELAY_MS, SessionExpiryConfirmDialogComponent.TIMER_INTERVAL_MS).pipe(
            map(timerVal => SessionExpiryConfirmDialogComponent.TIMER_LENGTH_S - timerVal), // Map to remaining time
            tap(remainingTime => {
                if (remainingTime <= 0) {
                    this.timerExpired.emit();
                }
            }),
        )),
        takeUntil(merge(this.sessionExtended, this.timerExpired)),
    );

    counter = toSignal(this.expiryTimer$, {initialValue: SessionExpiryConfirmDialogComponent.TIMER_LENGTH_S});

    constructor(
        public coreDialogService: CoreDialogService,
    ) {
        super(coreDialogService, 'SessionExpiryConfirm');
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.expiryTimer$.subscribe(),
        );
        this.expiryInitialization$.next();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    @HostListener('window:keyup.escape', ['$event']) onKeyDown(e: KeyboardEvent) {
        this.cancel();
    }

    /**
     * Action when the user clicks the "OK" button
     */
    ok(): void {
        this.sessionExtended.emit();
        this.closeDialog(true);
    }

    /**
     * Action when the user clicks the "Cancel" button, or closes the dialog via the "X" in the header
     */
    cancel(): void {
        this.closeDialog(false);
    }
}
